import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type { Program, ProgramLevel } from '@lms/shared/types';

export const programKeys = {
  all: ['programs'] as const,
  lists: () => [...programKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...programKeys.all, 'list', params] as const,
  details: () => [...programKeys.all, 'detail'] as const,
  detail: (id: string) => [...programKeys.details(), id] as const,
  levels: (id: string) => [...programKeys.detail(id), 'levels'] as const,
  overview: () => [...programKeys.all, 'overview'] as const,
  reports: (id: string) => [...programKeys.detail(id), 'reports'] as const,
};

export function useProgramsOverview() {
  return useQuery({
    queryKey: programKeys.overview(),
    queryFn: () => apiClient.programs.getOverview(),
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}

export function usePrograms(params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: programKeys.list((params as Record<string, unknown>) || {}),
    queryFn: () => apiClient.programs.list(params),
    staleTime: 15000,
    refetchOnWindowFocus: true,
  });
}

export function useProgram(id: string) {
  return useQuery({
    queryKey: programKeys.detail(id),
    queryFn: () => apiClient.programs.get(id),
    enabled: !!id,
  });
}

export function useProgramLevels(id: string) {
  return useQuery({
    queryKey: programKeys.levels(id),
    queryFn: () => apiClient.programs.getLevels(id),
    enabled: !!id,
  });
}

export function useCreateProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Program>) => apiClient.programs.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: programKeys.overview() });
    },
  });
}

export function useUpdateProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Program> }) =>
      apiClient.programs.update(id, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: programKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: programKeys.overview() });
      void queryClient.invalidateQueries({ queryKey: programKeys.reports(id) });
    },
  });
}

export function useDuplicateProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, code, name }: { id: string; code: string; name: string }) =>
      apiClient.programs.duplicate(id, { code, name }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: programKeys.overview() });
    },
  });
}

export function useDeleteProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.programs.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: programKeys.overview() });
    },
  });
}

export function useCreateLevel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, data }: { programId: string; data: Partial<ProgramLevel> }) =>
      apiClient.programs.createLevel(programId, data),
    onSuccess: (_, { programId }) => {
      void queryClient.invalidateQueries({ queryKey: programKeys.detail(programId) });
      void queryClient.invalidateQueries({ queryKey: programKeys.levels(programId) });
      void queryClient.invalidateQueries({ queryKey: programKeys.reports(programId) });
    },
  });
}

export function useUpdateLevel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      programId,
      levelId,
      data,
    }: {
      programId: string;
      levelId: string;
      data: Partial<ProgramLevel>;
    }) => apiClient.programs.updateLevel(programId, levelId, data),
    onSuccess: (_, { programId }) => {
      void queryClient.invalidateQueries({ queryKey: programKeys.detail(programId) });
      void queryClient.invalidateQueries({ queryKey: programKeys.levels(programId) });
      void queryClient.invalidateQueries({ queryKey: programKeys.reports(programId) });
    },
  });
}

export function useDeleteLevel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, levelId }: { programId: string; levelId: string }) =>
      apiClient.programs.deleteLevel(programId, levelId),
    onSuccess: (_, { programId }) => {
      void queryClient.invalidateQueries({ queryKey: programKeys.detail(programId) });
      void queryClient.invalidateQueries({ queryKey: programKeys.levels(programId) });
      void queryClient.invalidateQueries({ queryKey: programKeys.reports(programId) });
    },
  });
}

export function useProgramReport(id: string) {
  return useQuery({
    queryKey: programKeys.reports(id),
    queryFn: () => apiClient.programs.getReport(id),
    enabled: !!id,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

export function useExportProgramReport() {
  return useMutation({
    mutationFn: (id: string) => apiClient.programs.exportReport(id),
  });
}

export function useSyncProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.programs.sync(id),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: programKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: programKeys.reports(id) });
      void queryClient.invalidateQueries({ queryKey: programKeys.overview() });
      void queryClient.invalidateQueries({ queryKey: ['enrollments'] });
    },
  });
}
